import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import $api from "@/zapi-frontend/shared/api/axios";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { DockedChatIsland } from "@/zapi-frontend/shared/ui/ResponsiveChatPanel";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { PageLoader } from "@/zapi-frontend/shared/ui/PageLoader";
import { getRouteProjectDetails } from "@/zapi-frontend/shared/const/router";
import { ProjectCreationHeader } from "@/zapi-frontend/widgets/CreateProjectChat/ui/ProjectCreationHeader";
import { CaseBriefEditor } from "@/zapi-frontend/widgets/CreateProjectChat/ui/CaseBriefEditor";
import {
  CreateProjectChat,
  normalizeCaseBriefLanguageUserMessages,
  type ChatStep,
  type ChatUploadedFile,
  type CaseBriefCompleteness,
  type CreateProjectChatRef
} from "@/zapi-frontend/widgets/CreateProjectChat";
import { useCaseBriefChatStore } from "@/zapi-frontend/stores/caseBriefChatStore";
import type { TPreUploadedDocument } from "@/zapi-frontend/pages/CreateProjectPage/model/usePreUploadDocuments";
import type { TDocumentStatus } from "@/zapi-frontend/shared/types/shared";
import { InstructionsModal, type TInstructionsModalChapter } from "@/zapi-frontend/shared/ui/InstructionsModal/InstructionsModal";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";
import { useDocuments } from "@/zapi-frontend/shared/hooks/useDocuments";
import { useProjectDimensions } from "@/zapi-frontend/shared/services/useProjectDimensions";
import { useLanguages, LANGUAGE_CODE_TO_VALUE } from "@/zapi-frontend/shared/services/useLanguages";
import { useProjectSetupStore } from "@/zapi-frontend/stores/projectSetup";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { AnalysisSettingsModal } from "@/zapi-frontend/widgets/CreateProjectChat/ui/AnalysisSettingsModal";
import type { ProjectErrors, TProjectDataWithoutRepository } from "@/zapi-frontend/widgets/CreateProjectForm/model/types";
import { useChatStore } from "@/zapi-frontend/stores/chatStore";
import {
  ProjectSidebar,
  type TCreationView
} from "@/zapi-frontend/pages/ProjectDetailsPage/ui/ProjectSidebar/ProjectSidebar";
import { ProjectSetupForm } from "@/zapi-frontend/pages/CreateProjectPage/ui/ProjectSetupForm";
import {
  useChatUnreadIndicator,
  countVisibleAssistantMessages
} from "@/zapi-frontend/shared/hooks/useChatUnreadIndicator";
import { projectPageBackgroundStyle } from "@/zapi-frontend/shared/lib/projectTheme";
import { useProjectLanguageSync } from "@/zapi-frontend/shared/i18n/useProjectLanguageSync";

type TProjectResponse = {
  id: number;
  name: string;
  organizationId: number;
  versionDisplayName: string;
  versionGroupId: string | null;
  versions: { id: string; name: string; date: string; isDraft?: boolean }[];
  caseBriefMdContent: string | null;
  caseBriefId: number | null;
  projectInstructions: string | null;
  presentationPrompt: string | null;
  shortDescription: string | null;
  language: string;
  projectTemplateSlug: string;
  iconShape: string | null;
  iconColor: string | null;
  logoUrl: string | null;
  visibility: "organization" | "private";
  dimensionValues: Record<string, string>;
  projectChapters: Array<{
    id: number;
    chapterInstructions: string | null;
    projectTemplateChapter: {
      slug: string;
      name: string;
      icon?: string | null;
    } | null;
  }>;
  documents: Array<{
    id: number;
    name: string;
    status: TDocumentStatus;
    fileSize?: number;
    createdAt?: string;
    webUrl?: string | null;
  }>;
};

type TCaseBriefResponse = {
  mdContent: string | null;
  language: string;
  chatStep: string | null;
  pendingDimensionIndex: number | null;
  chatMessages: { id: string; role: "user" | "assistant"; content: string }[] | null;
  vectorStoreId: string | null;
  uploadedFiles: { fileId?: string; fileName: string; documentId: number; fileSize: number; webUrl?: string }[] | null;
  generatingStatus?: string | null;
  completeness?: CaseBriefCompleteness | null;
};

type TMode = "newVersion" | "relaunch";

type TProps = {
  mode?: TMode;
};

const AUTOSAVE_DEBOUNCE_MS = 1000;

const ALL_LANGUAGE_OPTIONS = [
  { label: "English", value: "english" },
  { label: "Japanese", value: "japanese" }
];

const LEGACY_VERSION_WELCOME = "Your draft is ready. Make your changes and launch the analysis when you're ready.";

// Carries the templateKey so the chat re-resolves it against the project language on every render.
const VERSION_WELCOME_CONTENT = JSON.stringify({
  type: "answer",
  templateKey: "versionDraftReady",
  content: LEGACY_VERSION_WELCOME
});

function isVersionWelcomeMessage(message: { role: string; content: string }): boolean {
  if (message.role !== "assistant") return false;
  try {
    const parsed = JSON.parse(message.content);
    return (
      parsed?.templateKey === "versionDraftReady" ||
      parsed?.type === "version_name_prompt" ||
      parsed?.content === LEGACY_VERSION_WELCOME
    );
  } catch {
    return false;
  }
}

// Tapping these tab/nav controls must not dismiss the open mobile chat island (INV-2388).
const CHAT_KEEP_OPEN_SELECTORS = [
  '[data-testid="sidebar-project-setup-button"]',
  '[data-testid="sidebar-case-frame-button"]',
  '[data-testid="project-creation-header-next-step-button"]',
  '[data-testid="project-creation-header-back-button"]'
];

export function NewVersionPage({ mode = "newVersion" }: TProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("createProject");
  const { t: tProjectHome } = useTranslation("projectHome");
  const { projectId } = useParams<{ projectId: string }>();
  const projectIdNum = Number(projectId);
  const { role, accessMode, isLabs } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);
  const { modelName: selectedModel, setModelName: setSelectedModel } = useChatStore();
  const setSetupTask = useProjectSetupStore((s) => s.setSetupTask);
  const setPolling = useProjectSetupStore((s) => s.setPolling);

  const queryClient = useQueryClient();
  const versionsQueryKey = useMemo(() => ["project-new-version", projectIdNum] as const, [projectIdNum]);

  const { data, isLoading, error, refetch } = useQuery<TProjectResponse>({
    queryKey: versionsQueryKey,
    queryFn: async () => {
      const res = await $api.get<{ data: TProjectResponse }>(`/projects/${projectIdNum}`);
      return res.data.data;
    },
    enabled: Number.isFinite(projectIdNum) && projectIdNum > 0
  });

  useProjectLanguageSync(data?.language);

  const handleOptimisticRename = useCallback(
    (versionId: string, newName: string) => {
      queryClient.setQueryData<TProjectResponse>(versionsQueryKey, (old) =>
        old
          ? {
              ...old,
              versionDisplayName: versionId === String(projectIdNum) ? newName : old.versionDisplayName,
              versions: old.versions?.map((v) => (v.id === versionId ? { ...v, name: newName } : v))
            }
          : old
      );
    },
    [queryClient, versionsQueryKey, projectIdNum]
  );

  const { data: orgDimensions = [] } = useProjectDimensions();
  const sortedDimensions = useMemo(
    () => [...orgDimensions].sort((a, b) => a.sortOrder - b.sortOrder),
    [orgDimensions]
  );

  const { languages } = useLanguages();
  const availableLanguages = useMemo(() => {
    const configuredValues = new Set(
      languages.map((l) => LANGUAGE_CODE_TO_VALUE[l.code] ?? l.code.toLowerCase()).filter(Boolean)
    );
    if (configuredValues.size === 0) return ALL_LANGUAGE_OPTIONS;
    return ALL_LANGUAGE_OPTIONS.filter((lang) => configuredValues.has(lang.value));
  }, [languages]);
  const { documents, createDocument, createWebLink, deleteDocument, refetchDocuments } = useDocuments(
    projectId || "",
    undefined,
    3000
  );

  const [content, setContent] = useState("");
  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [isLaunching, setIsLaunching] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [caseBriefId, setCaseBriefId] = useState<number | null>(null);
  const [isRestoringBrief, setIsRestoringBrief] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<TPreUploadedDocument[]>([]);
  const [versionDisplayName, setVersionDisplayName] = useState<string>("");
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false);
  const [isSavingInstructions, setIsSavingInstructions] = useState(false);
  const [completeness, setCompleteness] = useState<CaseBriefCompleteness | null>(null);
  const [initialGeneratingStatus, setInitialGeneratingStatus] = useState<string | null>(null);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [skipPrefill, setSkipPrefill] = useState(false);
  const [settingsErrors, setSettingsErrors] = useState<ProjectErrors>({});
  const [projectInstructions, setProjectInstructions] = useState<string>("");
  const [chaptersInstructions, setChaptersInstructions] = useState<Record<string, string>>({});
  const [presentationPrompt, setPresentationPrompt] = useState<string>("");
  const [dimensionValues, setDimensionValues] = useState<Record<string, string>>({});
  const [creationView, setCreationView] = useState<TCreationView>(
    mode === "relaunch" ? "caseFrame" : "projectSetup"
  );
  const [iconShape, setIconShape] = useState<string | null>(null);
  const [iconColor, setIconColor] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [visibility, setVisibility] = useState<"organization" | "private">("organization");
  const caseBriefRestored = useRef(false);
  const chatRef = useRef<CreateProjectChatRef | null>(null);

  useEffect(() => {
    if (!data || hydrated) return;
    setContent(data.caseBriefMdContent ?? "");
    setProjectName(data.name);
    setProjectDescription(data.shortDescription ?? "");
    setVersionDisplayName(data.versionDisplayName ?? "");
    setProjectInstructions(data.projectInstructions ?? "");
    setPresentationPrompt(data.presentationPrompt ?? "");
    setDimensionValues(data.dimensionValues ?? {});
    setIconShape(data.iconShape ?? null);
    setIconColor(data.iconColor ?? null);
    setLogoUrl(data.logoUrl ?? null);
    setVisibility(data.visibility ?? "organization");
    setChaptersInstructions(
      (data.projectChapters ?? []).reduce<Record<string, string>>((acc, ch) => {
        const slug = ch.projectTemplateChapter?.slug;
        if (slug) acc[slug] = ch.chapterInstructions ?? "";
        return acc;
      }, {})
    );
    setUploadedDocuments(
      (data.documents ?? []).map((doc) => ({
        documentId: doc.id,
        fileName: doc.name,
        status: doc.status,
        fileSize: doc.fileSize,
        createdAt: doc.createdAt ? new Date(doc.createdAt) : undefined,
        webUrl: doc.webUrl ?? undefined
      }))
    );
    setHydrated(true);
  }, [data, hydrated]);

  useEffect(() => {
    if (data?.versionDisplayName != null) setVersionDisplayName(data.versionDisplayName);
  }, [data?.versionDisplayName]);

  const chaptersForModal = useMemo<TInstructionsModalChapter[]>(() => {
    if (!data?.projectChapters) return [];
    return data.projectChapters
      .map((ch) => ({
        slug: ch.projectTemplateChapter?.slug ?? "",
        name: ch.projectTemplateChapter?.name ?? "",
        icon: ch.projectTemplateChapter?.icon ?? null
      }))
      .filter((ch) => !!ch.slug);
  }, [data?.projectChapters]);

  const patchInstructions = useCallback(
    (nextProjectInstructions: string, nextChaptersInstructions: Record<string, string>) => {
      setProjectInstructions(nextProjectInstructions);
      setChaptersInstructions(nextChaptersInstructions);
    },
    []
  );

  const patchPresentationPrompt = useCallback((value: string) => {
    setPresentationPrompt(value);
  }, []);

  const vaultDocuments: TPreUploadedDocument[] = useMemo(
    () =>
      documents.map((d) => ({
        documentId: d.id,
        fileName: d.fileName,
        fileSize: d.fileSize,
        webUrl: d.webUrl ?? undefined,
        status: d.status as TDocumentStatus
      })),
    [documents]
  );

  const handleDimensionChange = useCallback(
    (dimensionId: number, value: string) => {
      setDimensionValues((prev) => {
        const next = { ...prev };
        if (value.trim() === "") delete next[String(dimensionId)];
        else next[String(dimensionId)] = value;
        return next;
      });
      if (!Number.isFinite(projectIdNum)) return;
      const updated = { ...dimensionValues };
      if (value.trim() === "") delete updated[String(dimensionId)];
      else updated[String(dimensionId)] = value;
      $api
        .patch(`/projects/${projectIdNum}`, { dimensionValues: updated })
        .catch((err) => console.error("Failed to save dimension value:", err));
    },
    [projectIdNum, dimensionValues]
  );

  const handlePhotoChange = useCallback(
    async (logoBase64: string) => {
      if (!Number.isFinite(projectIdNum)) return;
      await $api.patch(`/projects/${projectIdNum}`, { logoBase64 });
      setLogoUrl(logoBase64);

      const { selectedProject, setSelectedProject } = useProjectStore.getState();
      if (selectedProject && selectedProject.id === String(projectIdNum)) {
        setSelectedProject({
          ...selectedProject,
          logoUrl: logoBase64,
          photoAuthor: null,
          photoLicense: null,
          photoSourceUrl: null
        });
      }
    },
    [projectIdNum]
  );

  const handleVisibilityChange = useCallback(
    (next: "organization" | "private") => {
      setVisibility(next);
      if (!Number.isFinite(projectIdNum)) return;
      $api
        .patch(`/projects/${projectIdNum}`, { visibility: next })
        .catch((err) => console.error("Failed to save project visibility:", err));
    },
    [projectIdNum]
  );

  const handleLanguageChange = useCallback(
    async (lang: string) => {
      queryClient.setQueryData<TProjectResponse>(versionsQueryKey, (old) =>
        old ? { ...old, language: lang } : old
      );
      useCaseBriefChatStore.getState().setLanguage(lang);
      if (!Number.isFinite(projectIdNum)) return;

      // The brief's headings and field labels are translated server-side, so the editor is
      // refreshed from the response rather than keeping the old language's structure. The
      // debounced autosave then carries the translated text onto the project row.
      if (caseBriefId && content) {
        try {
          const res = await $api.patch<{ mdContent?: string }>(
            "/chat/create-project-chat/update-case-brief-language",
            { caseBriefId, language: lang, mdContent: content }
          );
          if (res.data.mdContent) setContent(res.data.mdContent);
        } catch (err) {
          console.error("Failed to update case brief language:", err);
        }
      }

      $api
        .patch(`/projects/${projectIdNum}`, { language: lang })
        .catch((err) => console.error("Failed to save project language:", err));
    },
    [queryClient, versionsQueryKey, projectIdNum, caseBriefId, content]
  );

  useEffect(() => {
    if (!data?.caseBriefId || caseBriefRestored.current) return;
    caseBriefRestored.current = true;
    const cbId = data.caseBriefId;
    setIsRestoringBrief(true);
    useCaseBriefChatStore.getState().reset();
    useCaseBriefChatStore.getState().setCaseBriefId(cbId);
    setCaseBriefId(cbId);

    if (mode === "newVersion") {
      const welcomeMsg = {
        id: crypto.randomUUID(),
        role: "assistant" as const,
        content: VERSION_WELCOME_CONTENT
      };
      useCaseBriefChatStore.getState().setMessages([welcomeMsg]);
      useCaseBriefChatStore.getState().setStep("chat" as ChatStep);
    }

    $api
      .get<TCaseBriefResponse>(`/chat/create-project-chat/case-brief/${cbId}`)
      .then((res) => {
        if (res.data.language) useCaseBriefChatStore.getState().setLanguage(res.data.language);
        if (res.data.chatStep && res.data.chatStep !== "language") {
          useCaseBriefChatStore.getState().setStep(res.data.chatStep as ChatStep);
        }
        if (typeof res.data.pendingDimensionIndex === "number") {
          useCaseBriefChatStore.getState().setPendingDimensionIndex(res.data.pendingDimensionIndex);
        }
        if (res.data.vectorStoreId) {
          useCaseBriefChatStore.getState().setVectorStoreId(res.data.vectorStoreId);
        }
        setInitialGeneratingStatus(res.data.generatingStatus ?? null);
        if (res.data.completeness) setCompleteness(res.data.completeness);
        if (res.data.uploadedFiles && Array.isArray(res.data.uploadedFiles) && res.data.uploadedFiles.length > 0) {
          useCaseBriefChatStore.getState().setUploadedFiles(res.data.uploadedFiles);
          setUploadedDocuments((prev) => {
            const existingIds = new Set(prev.map((d) => d.documentId));
            const extra = res.data.uploadedFiles!.filter((f) => !existingIds.has(f.documentId)).map((f) => ({
              documentId: f.documentId,
              fileName: f.fileName,
              status: "done" as TDocumentStatus,
              fileSize: f.fileSize,
              webUrl: f.webUrl
            }));
            return extra.length > 0 ? [...prev, ...extra] : prev;
          });
        }
        const restoredMsgs = res.data.chatMessages ?? [];
        const normalizedRestored = restoredMsgs.map((m) =>
          isVersionWelcomeMessage(m) ? { ...m, content: VERSION_WELCOME_CONTENT } : m
        );
        const hasWelcome = normalizedRestored.some(isVersionWelcomeMessage);
        const hasUserMessage = normalizedRestored.some((m) => m.role === "user");
        const shouldInjectWelcome = mode === "newVersion" && !hasWelcome && !hasUserMessage;
        if (shouldInjectWelcome) {
          const welcomeMsg = {
            id: crypto.randomUUID(),
            role: "assistant" as const,
            content: VERSION_WELCOME_CONTENT
          };
          const merged = [welcomeMsg, ...normalizedRestored];
          useCaseBriefChatStore.getState().setMessages(normalizeCaseBriefLanguageUserMessages(merged));
        } else if (normalizedRestored.length > 0) {
          useCaseBriefChatStore.getState().setMessages(normalizeCaseBriefLanguageUserMessages(normalizedRestored));
        }
      })
      .catch((err) => console.error("[NewVersionPage] case brief restore failed:", err))
      .finally(() => setIsRestoringBrief(false));
  }, [data?.caseBriefId]);

  useEffect(() => {
    if (!hydrated || !data) return;
    const briefUnchanged = content === (data.caseBriefMdContent ?? "");
    const nameUnchanged = projectName === data.name;
    const descUnchanged = projectDescription === (data.shortDescription ?? "");
    if (briefUnchanged && nameUnchanged && descUnchanged) return;
    const timer = setTimeout(() => {
      $api
        .patch(`/projects/${projectIdNum}`, {
          caseBriefMdContent: content,
          ...(projectName.trim() && projectName !== data.name ? { name: projectName } : {}),
          ...(projectDescription !== (data.shortDescription ?? "") ? { shortDescription: projectDescription } : {})
        })
        .catch((err) => console.error("New Version autosave failed:", err));
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [content, projectName, projectDescription, data, hydrated, projectIdNum]);

  useEffect(() => {
    if (!hydrated || caseBriefId === null) return;
    if (content === (data?.caseBriefMdContent ?? "")) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      $api
        .post<CaseBriefCompleteness>("/chat/create-project-chat/completeness", {
          caseBriefContent: content,
          caseBriefId
        })
        .then((res) => {
          if (!cancelled) setCompleteness(res.data);
        })
        .catch((err) => console.error("Failed to fetch completeness:", err));
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [content, hydrated, caseBriefId, data?.caseBriefMdContent]);

  useEffect(() => {
    if (caseBriefId === null) return;
    const lastSavedSnapshotRef = { current: "" };
    const getSnapshot = (state: { messages: { id: string; content: string }[] }) => {
      const last = state.messages[state.messages.length - 1];
      return `${state.messages.length}:${last?.id ?? ""}:${last?.content?.length ?? 0}`;
    };
    const unsub = useCaseBriefChatStore.subscribe((state) => {
      if (isRestoringBrief) return;
      if (state.caseBriefId !== caseBriefId) return;
      if (!state.messages.length) return;
      if (!state.messages.some((m) => m.role === "user")) return;
      const snap = getSnapshot(state);
      if (snap === lastSavedSnapshotRef.current) return;
      lastSavedSnapshotRef.current = snap;
      $api
        .patch("/chat/create-project-chat/update-case-brief-content", {
          caseBriefId,
          mdContent: content,
          chatMessages: state.messages
        })
        .catch((err) => console.error("Failed to autosave chat messages:", err));
    });
    return unsub;
  }, [caseBriefId, content, isRestoringBrief]);

  const handleLaunch = async () => {
    if (!data) return;
    setIsLaunching(true);
    try {
      await $api.patch(`/projects/${projectIdNum}`, {
        projectInstructions,
        chaptersInstructions,
        presentationPrompt
      });
      const setupRes = await $api.post<{ taskId: number }>(`/projects/${projectIdNum}/auto-setup`, {
        prefill: !skipPrefill,
        ...(selectedModel ? { model: selectedModel } : {})
      });
      if (setupRes.data?.taskId) {
        setSetupTask(String(projectIdNum), setupRes.data.taskId);
        setPolling(String(projectIdNum), true);
      }
      toast.success(t("launch.launched"));
      navigate(getRouteProjectDetails(projectIdNum));
    } catch (err) {
      console.error("Failed to launch analysis:", err);
      toast.error(t("launch.failed"));
    } finally {
      setIsLaunching(false);
    }
  };

  const handleInstructionsSave = useCallback(async () => {
    if (!Number.isFinite(projectIdNum)) {
      setIsInstructionsOpen(false);
      return;
    }
    if (isSavingInstructions) return;
    setIsSavingInstructions(true);
    try {
      await $api.patch(`/projects/${projectIdNum}`, {
        projectInstructions,
        chaptersInstructions,
        presentationPrompt
      });
      setIsInstructionsOpen(false);
    } catch (err) {
      console.error("Failed to save instructions:", err);
      toast.error(t("instructionsSaveFailed"));
    } finally {
      setIsSavingInstructions(false);
    }
  }, [projectIdNum, projectInstructions, chaptersInstructions, presentationPrompt, isSavingInstructions, t]);

  const handleFileUploaded = (file: ChatUploadedFile) => {
    setUploadedDocuments((prev) => [
      ...prev,
      { documentId: file.documentId, fileName: file.fileName, status: "done" as TDocumentStatus, fileSize: file.fileSize }
    ]);
  };

  const handleRemoveDocument = (documentId: number) => {
    setUploadedDocuments((prev) => prev.filter((d) => d.documentId !== documentId));
  };

  const handleAddWebLink = (url: string) => {
    setUploadedDocuments((prev) => [
      ...prev,
      { documentId: -Date.now(), fileName: url, status: "done" as TDocumentStatus, webUrl: url }
    ]);
  };

  const ensureCaseBrief = async () => caseBriefId ?? 0;

  const assistantMessageCount = useCaseBriefChatStore((s) => countVisibleAssistantMessages(s.messages));
  const { hasUnread: hasUnreadChatMessages, markSeen: markChatSeen } = useChatUnreadIndicator(assistantMessageCount, {
    ready: caseBriefId !== null && !isRestoringBrief,
    unreadOnMount: true
  });

  if (isLoading || !data) {
    return <PageLoader className="h-[calc(100vh-56px)]" />;
  }

  if (error) {
    return (
      <div className="flex h-[calc(100vh-56px)] items-center justify-center text-innoveraZinc-600">
        Failed to load project.
      </div>
    );
  }

  const title = mode === "relaunch" ? t("titles.reviewRelaunch") : t("titles.newVersion");
  const caseBriefProgress = completeness?.overallScore ?? 0;
  const hasMinimumBrief = projectName.trim().length > 0 && !!data.language;
  const computedBriefStatus: "empty" | "incomplete" | "complete" = !hasMinimumBrief
    ? "empty"
    : caseBriefProgress >= 75
    ? "complete"
    : "incomplete";

  const setupDocuments = vaultDocuments.length > 0 ? vaultDocuments : uploadedDocuments;
  const setupFields = [
    !!projectName.trim(),
    !!projectDescription.trim(),
    !!data.language,
    ...sortedDimensions.map((dim) => !!dimensionValues[String(dim.id)]?.trim())
  ];
  const setupProgress = (setupFields.filter(Boolean).length / setupFields.length) * 100;

  const settingsProjectData: TProjectDataWithoutRepository = {
    name: projectName,
    description: projectDescription,
    language: data.language,
    projectTemplateSlug: data.projectTemplateSlug,
    chaptersInstructions,
    projectInstructions,
    presentationPrompt,
    dimensionValues
  };

  return (
    <div
      className={cn("flex flex-col bg-projectPage h-[calc(100vh-56px)] w-full overflow-hidden")}
      style={projectPageBackgroundStyle(iconColor)}
    >
      <div className="flex gap-2 flex-1 min-h-0 p-2">
        <ProjectSidebar
          creation={{
            view: creationView,
            onViewChange: (view) => {
              if (view === creationView) return;
              if (view === "caseFrame") chatRef.current?.enterCaseFrame();
              else chatRef.current?.enterProjectSetup();
            },
            caseFrameLocked: false
          }}
        />
        <div className="flex flex-col flex-1 bg-white overflow-hidden min-w-0">
          <ProjectCreationHeader
            title={creationView === "projectSetup" ? t("titles.projectSetup") : title}
            language={data?.language ?? null}
            progress={creationView === "projectSetup" ? setupProgress : caseBriefProgress}
            briefStatus={computedBriefStatus}
            canReset={false}
            isLaunching={isLaunching}
            primaryAction={creationView === "projectSetup" ? "next" : "launch"}
            onLaunch={handleLaunch}
            onNext={() => chatRef.current?.enterCaseFrame()}
            onBack={() => chatRef.current?.enterProjectSetup()}
            onReset={() => undefined}
            onOpenSettings={isLabs ? () => setIsSettingsModalOpen(true) : undefined}
            readOnly={!hasEdit}
          />
          {creationView === "projectSetup" ? (
            <ProjectSetupForm
              projectName={projectName}
              projectDescription={projectDescription}
              onProjectNameChange={setProjectName}
              onProjectDescriptionChange={setProjectDescription}
              iconShape={iconShape}
              iconColor={iconColor}
              logoUrl={logoUrl}
              onPhotoChange={handlePhotoChange}
              visibility={visibility}
              onVisibilityChange={handleVisibilityChange}
              language={data.language}
              languageOptions={availableLanguages}
              onLanguageChange={handleLanguageChange}
              vaultDocuments={setupDocuments}
              onVaultAddFiles={async (files) => {
                for (const file of Array.from(files)) {
                  await createDocument(file);
                }
                await refetchDocuments();
              }}
              onVaultAddWebLink={async (url) => {
                const trimmed = url.trim();
                const normalized = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
                await createWebLink(normalized);
                await refetchDocuments();
              }}
              onVaultRemove={async (documentId) => {
                await deleteDocument(documentId);
                await refetchDocuments();
              }}
              onInstructionsClick={hasEdit ? () => setIsInstructionsOpen(true) : undefined}
              dimensions={sortedDimensions}
              dimensionValues={dimensionValues}
              onDimensionChange={handleDimensionChange}
              versionLabel={versionDisplayName}
              readOnly={!hasEdit}
              caseBriefId={caseBriefId}
              projectId={projectIdNum}
            />
          ) : (
            <div className="flex-1 overflow-y-auto min-h-0">
              <CaseBriefEditor
                content={content}
                onUpdate={setContent}
                versionDisplayName={versionDisplayName}
                versionHistory={
                  data.organizationId != null
                    ? {
                        rowProjectId: String(projectIdNum),
                        projectName: data.name,
                        projectIconShape: data.iconShape,
                        projectIconColor: data.iconColor,
                        versions: data.versions ?? [],
                        hasEdit,
                        organizationId: data.organizationId,
                        onInvalidated: () => void refetch(),
                        onOptimisticRename: handleOptimisticRename
                      }
                    : undefined
                }
                showInstructionsChip={hasEdit}
                onInstructionsClick={hasEdit ? () => setIsInstructionsOpen(true) : undefined}
                readOnly={!hasEdit}
                caseBriefId={caseBriefId}
                projectId={projectIdNum}
              />
            </div>
          )}
        </div>

        <DockedChatIsland
          hasUnread={hasUnreadChatMessages}
          onOpenChange={markChatSeen}
          keepOpenSelectors={CHAT_KEEP_OPEN_SELECTORS}
        >
          {caseBriefId === null ? (
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between px-2 h-14 border-b border-innoveraZinc-100 shrink-0">
                <div className="flex items-center gap-1 px-3 py-2 h-8 rounded-lg">
                  <FigmaTypography component="span" variant="label-2" color="zinc500">
                    {tProjectHome("chat.title")}
                  </FigmaTypography>
                </div>
              </div>
              <div className="flex-1 flex items-center justify-center">
                <Spinner aria-label={tProjectHome("loading", { ns: "common" })} className="size-5 text-muted-foreground" />
              </div>
            </div>
          ) : (
            <CreateProjectChat
              chatRef={chatRef}
              caseBriefId={caseBriefId}
              isRestoring={isRestoringBrief}
              onNextStep={() => setCreationView("caseFrame")}
              onEnterProjectSetup={() => setCreationView("projectSetup")}
              onDocumentUpdate={setContent}
              onFileUploaded={handleFileUploaded}
              onRemoveDocument={handleRemoveDocument}
              onAddWebLink={handleAddWebLink}
              uploadedDocuments={uploadedDocuments}
              onEnsureCaseBrief={ensureCaseBrief}
              briefStatus={computedBriefStatus}
              isLaunching={isLaunching}
              onLaunchAnalysis={handleLaunch}
              progress={caseBriefProgress}
              currentBriefContent={content}
              externalLanguage={data.language}
              initialGeneratingStatus={initialGeneratingStatus}
            />
          )}
        </DockedChatIsland>
      </div>

      {isLabs && (
        <AnalysisSettingsModal
          isOpen={isSettingsModalOpen}
          onClose={() => setIsSettingsModalOpen(false)}
          onSave={({
            projectData: updated,
            selectedModel: updatedModel,
            skipPrefill: updatedSkipPrefill
          }) => {
            setProjectName(updated.name);
            setProjectDescription(updated.description ?? "");
            setProjectInstructions(updated.projectInstructions ?? "");
            setPresentationPrompt(updated.presentationPrompt ?? "");
            setChaptersInstructions(updated.chaptersInstructions ?? {});
            setDimensionValues(updated.dimensionValues ?? {});
            setSelectedModel(updatedModel);
            setSkipPrefill(updatedSkipPrefill);
          }}
          projectData={settingsProjectData}
          errors={settingsErrors}
          setErrors={setSettingsErrors}
          selectedModel={selectedModel}
          skipPrefill={skipPrefill}
        />
      )}

      <InstructionsModal
        open={isInstructionsOpen}
        onClose={() => {
          if (isSavingInstructions) return;
          setIsInstructionsOpen(false);
        }}
        onSave={handleInstructionsSave}
        isSaving={isSavingInstructions}
        chapters={chaptersForModal}
        projectInstructions={projectInstructions}
        chaptersInstructions={chaptersInstructions}
        presentationPrompt={presentationPrompt}
        onProjectInstructionsChange={patchInstructions}
        onPresentationPromptChange={patchPresentationPrompt}
      />
    </div>
  );
}
